import React, { useRef, useEffect, useCallback } from 'react';
import { UserData } from '../../store/dreamworldSlice';
import { X } from 'lucide-react';
import LoadingIndicator from '../ui/LoadingIndicator';

interface FollowersModalProps {
  users: UserData[];
  title: string;
  onClose: () => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

const FollowersModal: React.FC<FollowersModalProps> = ({ users, title, onClose, onLoadMore, hasMore, loading }) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (modalContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loading) {
        onLoadMore();
      }
    }
  }, [hasMore, loading, onLoadMore]);

  useEffect(() => {
    const modalContent = modalContentRef.current;
    if (modalContent) {
      modalContent.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (modalContent) {
        modalContent.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md flex flex-col" style={{height: '80vh'}}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <div ref={modalContentRef} className="space-y-4 overflow-y-auto">
          {users.map((user) => (
            <div key={user.uid} className="flex items-center space-x-4">
              <img
                src={user.avatarUrl || '/default-avatar.png'}
                alt={user.username}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <p className="font-semibold">{user.username}</p>
                <p className="text-sm text-gray-400">{user.email}</p>
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-center"><LoadingIndicator /></div>}
        </div>
      </div>
    </div>
  );
};

export default FollowersModal;
